import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Typography,
  Row,
  Col,
  Spin,
  message,
} from 'antd';
import {
  FormHelpers, Buckets, Files, ScheduleOfValues,
} from 'ontraccr-common';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { getContactAddressBooks } from 'ontraccr-common/lib/Contacts';
import { addFileToFileMap, rotateFile } from '../files/state/files.actions';
import fieldTypes from './FormBuilder/FormFields/formFields.types';
import { isAddressAttribute } from './FormBuilder/FormFields/Attribute.constants';

import LiveFeedFileUpload from '../dashboard/LiveFeed/LiveFeedFileUpload';
import FullPhoto from '../common/photos/FullPhoto';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import SectionDisplayHeader from './SectionDisplayHeader';
import CheckAllYesNoModal from './CheckAllYesNoModal';

import Colors from '../constants/Colors';
import { getFileType, downloadFile, visuallyDownloadFile, getFileSignedDownloadUrl } from '../files/fileHelpers';

import useDynamicAttribute from '../common/hooks/useDynamicAttribute';

import {
  preparePreloadedFormTriggerData,
  hasHiddenSectionStaticTextToPreload,
  PO_FORM_RESPONDER_MODE,
} from './ResponderHelpers';
import { getIdMap, uuid } from '../helpers/helpers';
import Analytics from '../helpers/Analytics';

import { isObjectEmpty } from '../common/helpers';
import useGetFormDropdownField from '../common/hooks/useGetFormDropdownField';
import { getLightWeightForms } from './state/forms.actions';
import { getBoardCardsForDropdown, getBoardsForDropdown } from '../boards/state/boards.actions';
import { getBuckets } from '../buckets/state/buckets.actions';
import {
  getExternalBuckets,
  getExternalContacts,
  getExternalCostcodes,
  getExternalCustomers,
  getExternalEquipment,
  getExternalLabels,
  getExternalPhases,
  getExternalProjects,
  getExternalScheduleOfValues,
  getExternalScheduleOfValueSections,
  getExternalUsers,
  getExternalVendors,
  getExternalProjectEquipment,
  getExternalLightWeightForms,
} from './ExternalForm/state/externalForm.actions';
import {
  getTemplateDropdownDataTypes,
  scanResponseProjectIds,
} from './formHelpers';

import useFormDropdownOptions from './hooks/useFormDropdownOptions';
import { getProjectIdMap } from './state/forms.selectors';
import { getStatuses } from '../statuses/state/statuses.actions';
import { getProjectBillingRates } from '../projectBillingRates/states/projectBillingRates.actions';
import { getProjectMaterials } from '../materials/state/materials.actions';
import useEntryCostcodes from './hooks/useEntryCostcodes';
import Permissions from '../auth/Permissions';
import { getSyncedSovRows } from '../projects/ProjectScheduleOfValues/helpers';

const {
  Text,
} = Typography;

const filterByDivision = (arr, divisionSet) => (
  arr.filter((item) => divisionSet.has(item.divisionId))
);

const interactiveFields = new Set([
  'attachment',
  'gpsLocation',
  'staticText',
]);

export default function FormResponseFields({
  mode,
  errors = {},
  sections,
  fileMap = {},
  initialData,
  initialCustomerIds,
  initialProjectIds,
  initialCostcodeIds,
  initialVendorIds,
  initialBucketId,
  initialCardId,
  initialCardTitle,
  initialCardBoardId,
  initialEquipmentId,
  initialEquipmentTitle,
  templateProjectId,
  templateCustomerId,
  visible = false,
  selectedFormId,
  responses,
  onResponsesChanged,
  onCustomerIdChanged,
  onProjectIdChanged,
  onVendorIdChanged,
  onBucketIdChanged,
  style = {},
  header,
  divisions,
  selectedProject,
  isExternalForm,
  templateId,
  onResponseSectionsChanged,
  isDisplay = false,
  sectionPermissionMap,
  parentForm,
  createTimeTableIds,
  timecardProjectId,
  dropdownFilterProjectIds,
  onFieldTrigger,
  timecardPermissionMap,
  formUserIds = [],
  onFieldsInitialLoadedChange,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const projects = useSelector((state) => state.projects.projects);
  const projectTypes = useSelector((state) => state.projects.projectTypes);
  const phases = useSelector((state) => state.costcodes.phases);
  const scheduleOfValues = useSelector((state) => state.projects.scheduleOfValues);
  const scheduleOfValueSections = useSelector((state) => state.projects.scheduleOfValueSections);
  const userToLabel = useSelector((state) => state.users.userToLabel);
  const customerToLabel = useSelector((state) => state.customers.customerToLabel);
  const vendorToLabel = useSelector((state) => state.vendors.vendorToLabel);
  const formTemplates = useSelector((state) => state.forms.templates);
  const boardCardsForDropdown = useSelector((state) => state.boards.boardCardsForDropdown);
  const boardsForDropdown = useSelector((state) => state.boards.boardsForDropdown);
  const equipment = useSelector((state) => state.equipment.equipment);
  const positionNames = useSelector((state) => state.settings.positionNames);
  const buckets = useSelector((state) => state.buckets.buckets);
  const bucketTemplates = useSelector((state) => state.buckets.bucketTemplates);
  const statuses = useSelector((state) => state.statuses.statuses);
  const form = useRef(null);

  const projectIdMap = useSelector(getProjectIdMap);
  const phaseIdMap = useMemo(() => getIdMap(phases), [phases]);
  const equipmentIdMap = useMemo(() => getIdMap(equipment), [equipment]);

  const [key, setKey] = useState(DateTime.local().toMillis());
  const [selectedFile, setSelectedFile] = useState();
  // Keeps track of which file field is currently active/viewed
  const [currFileFieldId, setCurrFileFieldId] = useState();
  // For table attachment cells: which row's attachments we're viewing
  const [currAttachmentRowId, setCurrAttachmentRowId] = useState();
  const [currIndex, setCurrIndex] = useState();
  const [previewProps, setPreviewProps] = useState({});
  const [fileAddId, setFileAddId] = useState();
  const [hasInitialLoaded, setHasInitialLoaded] = useState(false);
  const [scheduleOfValuesSummary, setScheduleOfValuesSummary] = useState();
  const [changeOrderMap, setChangeOrderMap] = useState();
  const [subContractMap, setSubContractMap] = useState();
  const [subContractCOMap, setSubContractCOMap] = useState();
  const [initialResponses, setInitialResponses] = useState({});
  const [collapsed, setCollapsed] = useState(new Array(sections?.length).fill(false));
  const [currFieldId, setCurrFieldId] = useState();
  const [bucketId, setBucketId] = useState();
  const [hasBucketsLoaded, setHasBucketsLoaded] = useState(false);
  const [dynamicLoading, setDynamicLoading] = useState(false);

  // attributes Id state
  const [customerIds, setCustomerIds] = useState();
  const [projectIds, setProjectIds] = useState();
  const [vendorIds, setVendorIds] = useState();
  const [contactIds, setContactIds] = useState();
  const [costcodeIds, setCostcodeIds] = useState();
  const [equipmentId, setEquipmentId] = useState(initialEquipmentId);
  const [userIds, setUserIds] = useState();
  const [subContractIds, setSubContractIds] = useState();
  const [payableSubContractIds, setPayableSubContractIds] = useState();
  const [phaseId, setPhaseId] = useState();
  const [checkAllYesNoSectionId, setCheckAllYesNoSectionId] = useState(null);

  const vendorId = useMemo(() => vendorIds?.[0], [vendorIds]);
  const customerId = useMemo(() => customerIds?.[0], [customerIds]);
  const projectId = useMemo(() => projectIds?.[0], [projectIds]);
  const userId = useMemo(() => userIds?.[0], [userIds]);

  const users = useSelector((state) => state.users.users);
  const materials = useSelector((state) => state.materials.materials);
  const projectMaterialsByProjectId = useSelector((state) => state.materials.projectMaterials);
  const customers = useSelector((state) => state.customers.customers);
  const projectEquipment = useSelector((state) => state.equipment.projectEquipment);
  const equipmentTypes = useSelector((state) => state.equipment.equipmentTypes);
  const vendors = useSelector((state) => state.vendors.vendors);
  const customTables = useSelector((state) => state.forms.customTables);
  const selectedFormTemplate = useSelector((state) => state.forms.selectedFormTemplate);
  const globalAddressBooks = useSelector((state) => state.contacts.globalAddressBooks);
  const divisionMap = useSelector((state) => state.settings.divisions);
  const labels = useSelector((state) => state.labels);
  const forms = useSelector((state) => state.forms.lightWeightForms);
  const subContractForms = useSelector((state) => state.forms.lightWeightSubContracts);
  const bucketTemplateMap = useSelector((state) => state.buckets.bucketTemplateMap);
  const { settings = {} } = useSelector((state) => state.settings.company);
  const globalFileMap = useSelector((state) => state.files.fileMap);
  const scheduleCategories = useSelector((state) => state.schedule.categories);
  const equipmentTypeMap = useMemo(() => getIdMap(equipmentTypes), [equipmentTypes]);

  const assignedProjectIds = useMemo(() => {
    const projectIds = new Set();
    const formUserIdSet = new Set(formUserIds);
    users?.forEach((user) => {
      if (user.id === Permissions.id || formUserIdSet.has(user.id)) {
        user.projects?.forEach(({ id }) => projectIds.add(id));
      }
    });
    return projectIds;
  }, [users, formUserIds]);

  const dynamicAttributeFieldMap = useMemo(() => (
    FormHelpers.getDynamicAttributesMap(sections)
  ), [sections]);

  const contactAddressBooks = useMemo(() => (
    getContactAddressBooks(globalAddressBooks)
  ), [globalAddressBooks]);

  const selectedFileType = useMemo(() => (
    selectedFile ? getFileType(selectedFile) : null
  ), [selectedFile]);

  const currentFiles = useMemo(() => {
    if (!currFileFieldId || !responses) return [];
    const response = responses[currFileFieldId];
    if (!response) return [];
    if (currAttachmentRowId != null) {
      const row = response?.values?.find((r) => r.id === currAttachmentRowId);
      return row?.attachments ?? [];
    }
    const rawFiles = response.files ?? [];
    return rawFiles.map((file) => {
      if (typeof file === 'string') return fileMap[file] ?? file;
      return file;
    });
  }, [currFileFieldId, currAttachmentRowId, responses, fileMap]);

  const responseProjectIds = useMemo(
    () => scanResponseProjectIds({ sections, responses }),
    [sections, responses],
  );

  // Costcodes trees need a project when the host did not pass one (Users /
  // Custom Fields) - only used for costcodes
  const costcodeProjectId = useMemo(() => (
    projectId
    ?? responseProjectIds[0]
    ?? selectedProject?.projectId
  ), [projectId, responseProjectIds, selectedProject]);

  const allFormProjectIds = useMemo(() => {
    const idSet = new Set([
      ...(projectIds?.length ? projectIds : []),
      ...(responseProjectIds?.length ? responseProjectIds : []),
      ...(timecardProjectId ? [timecardProjectId] : []),
    ].filter(Boolean));
    return Array.from(idSet);
  }, [projectIds, responseProjectIds, timecardProjectId]);

  // External form fillers are unauthenticated and cannot call the authenticated
  // /costcodes/projects and /costcodes/global routes - keep these empty so
  // useEntryCostcodes' internal fetches no-op, and use externalCostcodes instead
  // (populated by the getExternalCostcodes dispatch further below via the public
  // external/:templateId/costcodes route).
  const {
    costcodes: fetchedCostcodes,
    costcodeMap: fetchedCostcodeIdMap,
    costcodesLoading,
    fetchProjectCostcodes,
  } = useEntryCostcodes({
    projectIds: isExternalForm ? [] : allFormProjectIds,
    divisionIds: isExternalForm ? [] : divisions,
  });

  const externalCostcodes = useSelector((state) => state.costcodes.externalCostcodes);

  const formCostcodes = isExternalForm ? externalCostcodes : fetchedCostcodes;
  const costcodeIdMap = useMemo(() => (
    isExternalForm ? getIdMap(externalCostcodes) : fetchedCostcodeIdMap
  ), [isExternalForm, externalCostcodes, fetchedCostcodeIdMap]);

  const onAddFile = useCallback(
    async (file) => {
      if (file.type === 'application/pdf' && file instanceof File) {
        const headerBuffer = await file.slice(0, 65536).arrayBuffer();
        const headerText = new TextDecoder().decode(headerBuffer);
        const isEncrypted = /\/Encrypt\b/.test(headerText);

        const field = sections?.flatMap((s) => s.fields).find((f) => f.id === fileAddId);
        const appendOn = field?.configProps?.append;

        if (appendOn && isEncrypted) {
          message.warning('PDF is encrypted and will not be appended');
          return;
        }
      }

      onResponsesChanged((oldResponses) => {
        const { [fileAddId]: oldResponse = {} } = oldResponses;
        const { files: oldFiles = [] } = oldResponse;

        const fileObj = file;
        fileObj.autoSaveId = uuid();
        fileObj.rotation = 0;

        if (!file.existing) {
          Object.defineProperty(fileObj, 'name', {
            writable: true,
            value: `${DateTime.local().toMillis()}-${fileObj.name}`,
          });
        }

        const newFiles = [...oldFiles, fileObj];
        dispatch(addFileToFileMap(fileObj.autoSaveId, fileObj));
        Analytics.track('FormResponder/FileAdd');

        return {
          ...oldResponses,
          [fileAddId]: {
            ...oldResponse,
            files: newFiles,
          },
        };
      });
    },
    [fileAddId, sections, onResponsesChanged],
  );

  const externalTemplateId = isExternalForm ? templateId : null;

  const onNextFileClick = useCallback((isRight) => () => {
    const existingFiles = currentFiles;
    const newIndex = isRight ? currIndex + 1 : currIndex - 1;
    const isInvalid = isRight ? newIndex >= existingFiles.length : newIndex < 0;
    if (isInvalid) return;
    const ourFile = existingFiles[newIndex] || {};
    const trueId = ourFile.id || ourFile.autoSaveId;
    const globalFile = globalFileMap[trueId];
    if (!globalFile) {
      visuallyDownloadFile({
        file: ourFile,
        dispatch,
        externalTemplateId,
      });
    }

    setSelectedFile(ourFile);
    setCurrIndex(newIndex);
  }, [currIndex, currentFiles, externalTemplateId, globalFileMap]);

  const onUploadEnd = useCallback(() => setFileAddId(), []);
  const onCloseFile = useCallback(async (rotation = -1) => {
    const fileType = Files.getFileType(selectedFile);
    const fieldId = currFileFieldId || currFieldId;
    if (fileType === 'image' && selectedFile.id) {
      if (await dispatch(rotateFile(selectedFile.id, { rotation }, fileMap?.[selectedFile?.id]?.rotation))) {
        onResponsesChanged((prev) => {
          if (currAttachmentRowId != null) {
            const row = prev[fieldId]?.data?.find((r) => r.id === currAttachmentRowId);
            const savedFile = row?.attachments?.find(({ id }) => id === selectedFile.id);
            if (savedFile) savedFile.rotation = rotation;
          } else {
            const savedFile = prev[fieldId]?.files?.find(({ id }) => id === selectedFile.id);
            if (savedFile) savedFile.rotation = rotation;
          }
          return prev;
        });
      }
    } else if (fileType === 'image' && await dispatch(rotateFile(selectedFile.autoSaveId, { rotation, autoSaved: true }, -1))) {
      onResponsesChanged((prev) => {
        if (currAttachmentRowId != null) {
          const row = prev[fieldId]?.data?.find((r) => r.id === currAttachmentRowId);
          const savedFile = row?.attachments?.find(({ autoSaveId }) => autoSaveId === selectedFile.autoSaveId);
          if (savedFile) savedFile.rotation = rotation;
        } else {
          const savedFile = prev[fieldId]?.files?.find(({ autoSaveId }) => autoSaveId === selectedFile.autoSaveId);
          if (savedFile) savedFile.rotation = rotation;
        }
        return prev;
      });
    }
    setSelectedFile();
    setCurrIndex();
    setCurrFileFieldId();
    setCurrAttachmentRowId();
  }, [selectedFile, currFileFieldId, currFieldId, currAttachmentRowId]);

  const onDownloadFile = useCallback(async () => {
    if (!selectedFile) return;
    const trueId = selectedFile.id || selectedFile.autoSaveId;
    const globalFile = globalFileMap[trueId];
    const downloadOpts = {};
    if (globalFile instanceof File) {
      downloadOpts.fileObject = globalFile;
    } else if (selectedFile?.url) {
      downloadOpts.fileURL = selectedFile.url;
      downloadOpts.saveName = selectedFile.name;
    }

    // If no download opts, use file details and external template id
    if (!Object.keys(downloadOpts).length) {
      downloadOpts.fileDetails = selectedFile;
      downloadOpts.externalTemplateId = externalTemplateId;
    }
    await downloadFile(downloadOpts);
  }, [selectedFile, globalFileMap, externalTemplateId]);

  const {
    relevantCompletedField,
    relevantSubContractField,
  } = useGetFormDropdownField({ sections });

  useEffect(() => {
    if (projectId) {
      dispatch(getProjectBillingRates(projectId));
      dispatch(getProjectMaterials(projectId));
    }
  }, [projectId]);

  const projectMaterials = projectId
    ? projectMaterialsByProjectId[projectId]
    : undefined;

  useEffect(() => {
    if ((relevantCompletedField || relevantSubContractField) && visible) {
      const {
        configProps: {
          subDataType: completedSubDataType,
          subDataTypeFilter: completedSubDataTypeFilter,
        } = {},
      } = relevantCompletedField ?? {};
      const {
        configProps: {
          subDataType: subContractSubDataType,
          subDataTypeFilter: subContractSubDataTypeFilter,
        } = {},
      } = relevantSubContractField ?? {};

      const params = {
        templateId: completedSubDataType,
        statusIds: completedSubDataTypeFilter,
        subContractTemplateId: subContractSubDataType,
        subContractStatusIds: subContractSubDataTypeFilter,
      };

      if (isExternalForm) {
        dispatch(getExternalLightWeightForms(selectedFormTemplate?.id, params));
      } else {
        dispatch(getLightWeightForms(params));
      }
    }
  }, [
    relevantCompletedField,
    relevantSubContractField,
    visible,
    isExternalForm,
    selectedFormTemplate,
  ]);

  useEffect(() => {
    if (!isExternalForm) {
      dispatch(getStatuses());
    }
  }, [isExternalForm]);

  useEffect(() => {
    const getBucketData = async (bucketTemplateIds) => {
      const params = { bucketTemplateIds: Array.from(bucketTemplateIds) };
      if (isExternalForm) {
        await dispatch(
          getExternalBuckets(
            selectedFormTemplate?.id,
            params,
          ),
        );
      } else {
        await dispatch(getBuckets(params));
      }
      setHasBucketsLoaded(true);
    };

    const bucketTemplateIds = new Set();
    sections?.forEach(({ fields = [] }) => {
      fields?.forEach((field) => {
        const { selectedType, configProps: { dataType, subDataType } = {} } = field;
        if (selectedType === 'dropdown') {
          if (dataType === 'Buckets') bucketTemplateIds.add(subDataType);
        }
      });
    });

    if (bucketTemplateIds.size) {
      getBucketData(bucketTemplateIds);
    }
  }, [sections, isExternalForm, selectedFormTemplate]);

  useEffect(() => {
    const boardIds = new Set();
    let hasBoardsDropdown = false;
    sections?.forEach(({ fields = [] }) => {
      fields?.forEach((field) => {
        const { selectedType, configProps: { dataType, subDataType } = {} } = field;
        if (selectedType === 'dropdown') {
          if (dataType === 'Cards' && subDataType) boardIds.add(subDataType);
          if (dataType === 'Boards') hasBoardsDropdown = true;
        }
      });
    });

    const filteredBoardIds = Array.from(boardIds)
      .filter((boardId) => boardId && !boardCardsForDropdown[boardId]);

    if (!isExternalForm && filteredBoardIds.length) {
      dispatch(getBoardCardsForDropdown(filteredBoardIds));
    }
    if (!isExternalForm && hasBoardsDropdown) {
      dispatch(getBoardsForDropdown());
    }
  }, [sections, isExternalForm, boardCardsForDropdown]);

  useEffect(() => {
    if (!visible) {
      setSelectedFile();
      setHasBucketsLoaded(false);
    }
  }, [visible]);

  useEffect(() => {
    // Preload responses
    const {
      id: parentFormId,
      templateId: parentTemplateId,
    } = parentForm ?? {};

    if (!visible || hasInitialLoaded) return;

    const needsHiddenStaticTextPreload = hasHiddenSectionStaticTextToPreload({
      sections: sections ?? [],
      sectionPermissionMap,
    });

    if (
      (!initialData || !fileMap)
      && !templateProjectId
      && !templateCustomerId
      && !parentFormId
      && (!initialCardId || !initialCardTitle)
      && (!initialEquipmentId || !initialEquipmentTitle)
      && !needsHiddenStaticTextPreload
    ) {
      if (!selectedFormId) {
        setHasInitialLoaded(true);
      }
      return;
    }

    const newResponses = preparePreloadedFormTriggerData({
      initialData: initialData || {},
      fileMap,
      sections: sections ?? [],
      templateProjectId,
      templateCustomerId,
      parentFormId,
      parentTemplateId,
      projectIdMap,
      customerIdMap: customers,
      formTemplateMap: formTemplates,
      initialCardId,
      initialCardTitle,
      initialCardBoardId,
      initialEquipmentId,
      initialEquipmentTitle,
      equipmentIdMap,
      sectionPermissionMap,
    });

    Analytics.track('FormResponder/Preload');
    setInitialResponses(newResponses);
    onResponsesChanged(newResponses);
    setHasInitialLoaded(true);
  }, [
    visible,
    hasInitialLoaded,
    selectedFormId,
    sections,
    initialData,
    fileMap,
    templateProjectId,
    templateCustomerId,
    projectIdMap,
    customers,
    parentForm,
    formTemplates,
    initialCardId,
    initialCardTitle,
    initialCardBoardId,
    initialEquipmentId,
    initialEquipmentTitle,
    equipmentIdMap,
    sectionPermissionMap,
  ]);

  useEffect(() => {
    // Preload responses for PO_FORM_RESPONDER_MODE:
    // Required to cover edge case due to form being selected after this component
    // Guarded to only run in PO_FORM_RESPONDER_MODE
    if (!visible || mode !== PO_FORM_RESPONDER_MODE || isObjectEmpty(selectedFormTemplate)) return;
    const { projectId: selectedProjectId } = selectedFormTemplate || {};
    const isProjectFormTemplate = !!selectedProjectId;
    if (isProjectFormTemplate) {
      const newResponses = preparePreloadedFormTriggerData({
        initialData: initialData || {},
        fileMap,
        sections: sections ?? [],
        templateProjectId,
        templateCustomerId,
        projectIdMap,
        customerIdMap: customers,
        equipmentIdMap,
        sectionPermissionMap,
      });
      setInitialResponses(newResponses);
      onResponsesChanged(newResponses);
    }
  }, [visible, mode, selectedFormTemplate, equipmentIdMap, sectionPermissionMap]);

  const onPreviewPropsChanged = useCallback((fieldId) => (newProps) => {
    setPreviewProps({
      ...previewProps,
      [fieldId]: newProps,
    });
  }, [previewProps]);

  const onCollapsedChanged = useCallback((sectionIndex) => () => {
    const newCollapsed = [...collapsed];
    newCollapsed[sectionIndex] = !collapsed[sectionIndex];
    setCollapsed(newCollapsed);
  }, [collapsed]);

  useEffect(() => {
    // Calculate field values for 'calculation' fields
    if (!hasInitialLoaded) return;
    onResponsesChanged((oldResponses) => {
      const {
        newResponses: linkedResponses,
        shouldUpdate: didUpdateLinkedTables,
      } = FormHelpers.syncLinkedEquipmentTables({ 
        responses: oldResponses, 
        sections: sections ?? [],
      });
      const {
        newResponses,
        shouldUpdate,
      } = FormHelpers.calculateValues({
        responses: didUpdateLinkedTables ? linkedResponses : oldResponses,
        sections: sections ?? [],
        settings,
      });
      if (!didUpdateLinkedTables && !shouldUpdate) return oldResponses;
      return newResponses;
    });
  }, [hasInitialLoaded, responses, sections, projectId, settings]);

  useDynamicAttribute({
    type: 'projects',
    hasInitialLoaded,
    setDynamicLoading,
    currentId: projectId,
    dynamicAttributeFieldMap: dynamicAttributeFieldMap?.projects,
    onResponsesChanged,
    sections,
    responses,
  });

  useDynamicAttribute({
    type: 'equipment',
    hasInitialLoaded,
    setDynamicLoading,
    currentId: equipmentId,
    dynamicAttributeFieldMap: dynamicAttributeFieldMap?.equipment,
    onResponsesChanged,
    sections,
    responses,
  });

  useDynamicAttribute({
    type: 'customers',
    hasInitialLoaded,
    setDynamicLoading,
    currentId: customerId,
    dynamicAttributeFieldMap: dynamicAttributeFieldMap?.customers,
    onResponsesChanged,
    sections,
    responses,
  });

  useDynamicAttribute({
    type: 'users',
    hasInitialLoaded,
    setDynamicLoading,
    currentId: userId,
    dynamicAttributeFieldMap: dynamicAttributeFieldMap?.users,
    onResponsesChanged,
    sections,
    responses,
  });

  useEffect(() => {
    // Need to reset project dropdown when customer changes
    if (!customerId) return;
    const projectFields = [];
    sections?.forEach(({ fields = [] }) => {
      fields.forEach((field) => {
        const {
          selectedType,
          configProps: { dataType, shouldDisableSmartFiltering = false } = {},
          id: fieldId,
        } = field;
        if (selectedType === 'dropdown' && dataType === 'Projects' && !shouldDisableSmartFiltering) {
          projectFields.push(fieldId);
        }
      });
    });
    const newResponses = { ...responses };
    let shouldUpdate = false;
    projectFields.forEach((fieldId) => {
      const {
        [fieldId]: {
          values = [],
        } = {},
      } = newResponses;
      const filteredValues = values.filter((val) => {
        const { id: valueProjectId } = val;
        const {
          [valueProjectId]: {
            customerId: projectCustomerId,
          } = {},
        } = projectIdMap;
        return projectCustomerId === customerId;
      });
      if (filteredValues.length !== values.length && values.length > 0) {
        shouldUpdate = true;
        newResponses[fieldId] = { values: filteredValues };
      }
    });
    if (shouldUpdate) {
      Analytics.track('FormResponder/CustomerProjectChange');
      onResponsesChanged(newResponses);
    }
  }, [sections, responses, customerId, projectIdMap]);

  useEffect(() => {
    if (onCustomerIdChanged) onCustomerIdChanged([customerId]);
  }, [customerId]);

  useEffect(() => {
    if (onVendorIdChanged) onVendorIdChanged(vendorId);
  }, [vendorId]);

  const projectIdChanged = (newProjectIds) => {
    setProjectIds(newProjectIds);
    if (onProjectIdChanged) onProjectIdChanged(newProjectIds);
  };

  const bucketIdChanged = (newBucketId) => {
    setBucketId(newBucketId);
    if (onBucketIdChanged) onBucketIdChanged([newBucketId]);
  };

  useEffect(() => {
    if (visible && initialCustomerIds?.length) setCustomerIds(initialCustomerIds);
  }, [visible, initialCustomerIds]);

  useEffect(() => {
    if (visible && initialProjectIds?.length) setProjectIds(initialProjectIds);
  }, [visible, initialProjectIds]);

  useEffect(() => {
    if (visible && initialCostcodeIds?.length) setCostcodeIds(initialCostcodeIds);
  }, [visible, initialCostcodeIds]);

  useEffect(() => {
    if (visible && initialVendorIds?.length) setVendorIds(initialVendorIds);
  }, [visible, initialVendorIds]);

  useEffect(() => {
    if (visible) setBucketId(initialBucketId);
  }, [visible, initialBucketId]);

  useEffect(() => {
    onFieldsInitialLoadedChange?.(visible && hasInitialLoaded);
  }, [visible, hasInitialLoaded, onFieldsInitialLoadedChange]);

  useEffect(() => {
    if (!visible) {
      Analytics.track('FormResponder/Close');
      setKey(DateTime.local().toMillis());
      onResponsesChanged({});
      setInitialResponses({});
      setBucketId();
      setHasInitialLoaded(false);
      setScheduleOfValuesSummary();
      setCustomerIds();
      setProjectIds();
      setVendorIds();
      setContactIds();
      setCostcodeIds();
      setUserIds();
      setSubContractIds();
      setPayableSubContractIds();
      setPhaseId();
      setEquipmentId();
    }
  }, [visible]);

  useEffect(() => {
    if (isExternalForm) {
      const dataTypes = getTemplateDropdownDataTypes(selectedFormTemplate);
      dataTypes.forEach((dataType) => {
        switch (dataType) {
          case 'Costcodes':
            dispatch(getExternalCostcodes(selectedFormTemplate?.id));
            dispatch(getExternalPhases(selectedFormTemplate?.id));
            break;
          case 'Contacts':
            dispatch(getExternalContacts(selectedFormTemplate?.id));
            break;
          case 'Customers':
            dispatch(getExternalCustomers(selectedFormTemplate?.id));
            break;
          case 'Equipment':
            dispatch(getExternalEquipment(selectedFormTemplate?.id));
            dispatch(getExternalProjectEquipment(selectedFormTemplate?.id));
            break;
          case 'Labels':
            dispatch(getExternalLabels(selectedFormTemplate?.id));
            break;
          case 'Projects':
            dispatch(getExternalProjects(selectedFormTemplate?.id));
            break;
          case 'Sub-Contracts':
            dispatch(getExternalScheduleOfValues(selectedFormTemplate?.id));
            dispatch(getExternalScheduleOfValueSections(selectedFormTemplate?.id));
            break;
          case 'Users':
            dispatch(getExternalUsers(selectedFormTemplate?.id));
            break;
          case 'Vendors':
            dispatch(getExternalVendors(selectedFormTemplate?.id));
            break;
          default:
            break;
        }
      });
    }
  }, [isExternalForm, selectedFormTemplate]);

  useEffect(() => {
    // Do not collapse sections if they have been manually expanded
    if (hasInitialLoaded) return;
    if (sections?.length) {
      setCollapsed(sections.map((section) => (
        section?.settings?.defaultCollapsed
      )));
    } else {
      setCollapsed([]);
    }
  }, [sections]);

  const scheduleOfValueSectionMap = useMemo(() => (
    getIdMap(scheduleOfValueSections)
  ), [scheduleOfValueSections]);

  useEffect(() => {
    let mostRecentValue;
    const projectChangeOrderMap = {};
    const projectSubContractMap = {};
    const subContractChangeOrderMap = {};
    const summary = {};

    Object.values(projectIdMap).forEach((project) => {
      summary[project.id] = {
        projectId: project.id,
        totalBaseContract: project.totalContractAmount,
        totalChanges: 0,
        totalCompletedToDate: 0,
        totalBilledToDate: 0,
        totalHoldbackToDate: 0,
        holdbackPercentage: project.holdbackPercentage,
        contractTotal: 0,
        invoiceAmount: 0,
        invoices: [],
      };
    });

    // Rows are summed once here: the latest row of every ledger key, with each
    // parent that owns sub-items carrying their sum.
    const syncedRows = getSyncedSovRows(scheduleOfValues);

    Object.keys(scheduleOfValues).forEach((sovKey) => {
      mostRecentValue = syncedRows[sovKey];

      if (mostRecentValue.isChangeOrder) {
        if (!projectChangeOrderMap[mostRecentValue.projectId]) {
          projectChangeOrderMap[mostRecentValue.projectId] = [];
        }
        if (mostRecentValue.isSubContract) {
          if (!(mostRecentValue.parentRowId in subContractChangeOrderMap)) {
            subContractChangeOrderMap[mostRecentValue.parentRowId] = {
              changes: [],
              totalChanges: 0,
            };
          }
          subContractChangeOrderMap[mostRecentValue.parentRowId].changes.push(mostRecentValue);
          subContractChangeOrderMap[mostRecentValue.parentRowId]
            .totalChanges += mostRecentValue.contractAmount;
        }

        projectChangeOrderMap[mostRecentValue.projectId].push(mostRecentValue);
      } else if (mostRecentValue.isSubContract) {
        if (!projectSubContractMap[mostRecentValue.projectId]) {
          projectSubContractMap[mostRecentValue.projectId] = [];
        }

        projectSubContractMap[mostRecentValue.projectId].push(mostRecentValue);
      }

      const project = projectIdMap[mostRecentValue.projectId];
      if (!project) return;
      const {
        totalContractAmount: totalBaseContract = 0,
        holdbackPercentage = 0.1,
      } = project || {};

      if (!(mostRecentValue.projectId in summary)) {
        summary[mostRecentValue.projectId] = {
          projectId: mostRecentValue.projectId,
          totalBaseContract,
          totalChanges: 0,
          totalCompletedToDate: 0,
          totalBilledToDate: 0,
          totalHoldbackToDate: 0,
          holdbackPercentage,
          contractTotal: 0,
          invoiceAmount: 0,
          invoices: [],
        };
      }

      const projectSummary = summary[mostRecentValue.projectId];

      if (mostRecentValue.rowId === 'log') {
        projectSummary.invoices.push(mostRecentValue);
        return;
      }

      if (mostRecentValue.isChangeOrder) {
        projectSummary.totalChanges += mostRecentValue.contractAmount;
      }

      // The parent row now carries the sum of its sub-items, so counting the
      // sub-items too would double count.
      if (ScheduleOfValues.isSubItem(mostRecentValue)) return;

      const relevantSection = scheduleOfValueSectionMap[mostRecentValue.sectionId];
      if (!relevantSection || relevantSection.shouldBeInCalculations) {
        projectSummary.totalBilledToDate += mostRecentValue.previousBillings;
        projectSummary.invoiceAmount += mostRecentValue.invoiceAmount;
      }
      projectSummary.totalHoldbackToDate += mostRecentValue.holdbackAmount;
    });

    Object.values(summary).forEach((projectSummary) => {
      // eslint-disable-next-line no-param-reassign
      projectSummary.contractTotal = projectSummary.totalBaseContract + projectSummary.totalChanges;
      // eslint-disable-next-line no-param-reassign
      projectSummary.totalCompletedToDate = (
        projectSummary.totalBilledToDate + projectSummary.invoiceAmount
      ) / projectSummary.contractTotal;
    });

    setScheduleOfValuesSummary(summary);
    setChangeOrderMap(projectChangeOrderMap);
    setSubContractMap(projectSubContractMap);
    setSubContractCOMap(subContractChangeOrderMap);
  }, [scheduleOfValues, projectIdMap, projectId, scheduleOfValueSectionMap]);

  const customerList = useMemo(() => Object.values(customers), [customers]);
  const vendorList = useMemo(() => Object.values(vendors), [vendors]);
  const divisionSet = useMemo(() => new Set(divisions), [divisions]);
  const divisionProjects = useMemo(() => (
    filterByDivision(projects, divisionSet)
  ), [projects, divisionSet]);

  const divisionEquipment = useMemo(() => (
    equipment.filter((eq) => eq.divisionIds.some((divId) => divisionSet.has(divId)))
  ), [equipment, divisionSet]);

  const divisionCostcodes = useMemo(() => (
    filterByDivision(formCostcodes, divisionSet)
  ), [formCostcodes, divisionSet]);

  const divisionMaterials = useMemo(() => {
    const divMaterials = {};
    Object.values(materials).forEach((mat) => {
      if (divisionSet.has(mat.divisionId)) {
        divMaterials[mat.id] = mat;
      }
    });
    return divMaterials;
  }, [materials, divisionSet]);

  const divUsers = useMemo(() => {
    const allUsers = divisions.map((divisionId) => {
      const {
        [divisionId]: {
          users: divisionUsers = new Set(),
        } = {},
      } = divisionMap;
      return Array.from(divisionUsers);
    });
    const userSet = new Set(allUsers.flat());
    // Users are already filtered by division when retrieving from backend for externalForms
    return users.filter((user) => user.active && (isExternalForm || userSet.has(user.id)));
  }, [users, divisions, divisionMap, isExternalForm]);

  const userIdMap = useMemo(() => getIdMap(divUsers), [divUsers]);

  const divisionFormTemplates = useMemo(
    () => Object
      .values(formTemplates)
      .filter(({ divisionId }) => divisionSet.has(divisionId)),
    [formTemplates, divisionSet],
  );

  const copySection = useCallback((sectionId, newName) => () => {
    const relevantSectionIndex = sections?.findIndex(({ id }) => id === sectionId) ?? -1;
    if (relevantSectionIndex < 0) return;
    const duplicated = FormHelpers.duplicateSection(sections[relevantSectionIndex]);
    if (newName) duplicated.name = newName;
    const newSections = [...sections];
    newSections.splice(relevantSectionIndex + 1, 0, duplicated);
    setCollapsed((prev) => {
      const newCollapsed = [...prev];
      newCollapsed.splice(relevantSectionIndex + 1, 0, false);
      return newCollapsed;
    });
    onResponseSectionsChanged(newSections);
  }, [sections, onResponseSectionsChanged]);

  const onSectionRenamed = useCallback((sectionId, newName) => {
    const newSections = sections.map((section) => {
      if (section.id !== sectionId) return section;
      return { ...section, name: newName };
    });
    onResponseSectionsChanged(newSections);
  }, [sections, onResponseSectionsChanged]);

  const onDeleteClicked = useCallback((sectionId) => () => {
    const relevantSectionIndex = sections?.findIndex(({ id }) => id === sectionId) ?? -1;
    const relevantSection = relevantSectionIndex >= 0 ? sections[relevantSectionIndex] : null;
    if (!relevantSection) return;
    CustomConfirmModal({
      title: `Remove Section ${relevantSection.name}?`,
      onOk: () => {
        const newSections = sections.filter(({ id }) => id !== sectionId);
        setCollapsed((prev) => {
          const newCollapsed = [...prev];
          newCollapsed.splice(relevantSectionIndex, 1);
          return newCollapsed;
        });
        onResponseSectionsChanged(newSections);
      },
      okText: 'Remove',
    });
  }, [sections, onResponseSectionsChanged]);

  const checkAllYesNo = useCallback((sectionId) => () => {
    setCheckAllYesNoSectionId(sectionId);
  }, []);

  const handleCheckAllYesNoConfirm = useCallback((isYes, sectionId) => {
    if (!sectionId) return;

    const relevantSection = sections?.find(({ id }) => id === sectionId);
    if (!relevantSection) return;

    const { fields = [] } = relevantSection;
    const newResponses = { ...responses };

    // Find all Yes/No fields in the section that are visible
    fields.forEach(({ id: fieldId, configProps, selectedType }) => {
      if (selectedType !== 'yes-no') return;

      const {
        hasConditionalRendering,
        conditionalRenderingFormula,
      } = configProps || {};

      const isFieldVisible = !hasConditionalRendering
        || !FormHelpers.isConditionalRenderingFormulaComplete(conditionalRenderingFormula)
        || FormHelpers.executeConditionalRenderingFormula({
          formula: conditionalRenderingFormula,
          responses,
        });

      if (!isFieldVisible) return;

      const value = isYes ? 'yes' : 'no';

      newResponses[fieldId] = {
        ...(newResponses[fieldId] || {}),
        value,
      };
    });

    onResponsesChanged(newResponses);
    setCheckAllYesNoSectionId(null);
  }, [sections, responses, onResponsesChanged]);

  const selectedBucket = useMemo(() => (
    Object.values(bucketTemplateMap).flat().find((bucket) => bucket.id === bucketId)
  ), [bucketId, bucketTemplateMap]);

  const {
    selectedBucketTypeToIdMap,
    selectedBucketTypes,
  } = useMemo(() => (
    Buckets.getRelevantSelectedBucketDetails({ responses, sections })
  ), [responses, sections]);

  // Stable key so order/map-identity churn does not rebuild selectedBuckets
  const selectedBucketIdsKey = useMemo(() => (
    Array.from(new Set(
      Object.values(selectedBucketTypeToIdMap ?? {}).flat(),
    )).sort().join(',')
  ), [selectedBucketTypeToIdMap]);

  // All buckets selected across Buckets dropdown fields (for UNION/OR smart filtering)
  const selectedBuckets = useMemo(() => {
    if (!selectedBucketIdsKey) return [];
    const selectedIds = new Set(selectedBucketIdsKey.split(','));
    return Object.values(bucketTemplateMap ?? {}).flat()
      .filter((bucket) => selectedIds.has(bucket.id));
  }, [selectedBucketIdsKey, bucketTemplateMap]);

  const relevantGPSBucketFieldMap = useMemo(() => {
    const fieldMap = sections?.reduce((acc, section) => {
      const { fields = [], id: sectionId } = section;
      fields?.forEach((field) => {
        const { id: fieldId } = field;
        acc[fieldId] = {
          ...field,
          sectionId,
          fieldId,
        };
      });
      return acc;
    }, {});

    const gpsBucketFieldMap = {};
    sections?.forEach(({ fields = [], id: sectionId }) => {
      fields.forEach((field) => {
        const {
          selectedType,
          configProps: { bucketField } = {},
          id: fieldId,
        } = field;
        if (selectedType === 'gpsLocation') {
          const relevantField = fieldMap[bucketField];
          if (relevantField?.sectionId !== sectionId && relevantField) {
            const fields = Object.values(fieldMap);
            const relevantFieldInSection = fields.find((field) => (
              field.duplicatedParentId === bucketField && field.sectionId === sectionId
            ));

            if (relevantFieldInSection) {
              gpsBucketFieldMap[fieldId] = relevantFieldInSection.id ?? relevantFieldInSection.fieldId;
            }
          }
        }
      });
    });

    return gpsBucketFieldMap;
  }, [sections]);

  // Check which dropdown types are actually used in this form
  const { hasCustomerDropdowns, hasProjectDropdowns } = useMemo(() => {
    if (!sections) return { hasCustomerDropdowns: false, hasProjectDropdowns: false };

    let hasCustomer = false;
    let hasProject = false;

    for (let i = 0; i < sections.length; i += 1) {
      const { fields } = sections[i];
      const safeFields = fields ?? [];
      for (let j = 0; j < safeFields.length; j += 1) {
        const { selectedType, configProps: { dataType, fieldDataType } = {} } = safeFields[j] ?? {};
        if (selectedType === 'dropdown' || selectedType === 'dynamicAttribute') {
          if (dataType === 'Customers' || fieldDataType === 'Customers') hasCustomer = true;
          if (dataType === 'Projects' || fieldDataType === 'Projects') hasProject = true;
          // Early exit if both found
          if (hasCustomer && hasProject) {
            return { hasCustomerDropdowns: true, hasProjectDropdowns: true };
          }
        }
      }
    }

    return { hasCustomerDropdowns: hasCustomer, hasProjectDropdowns: hasProject };
  }, [sections]);

  const {
    customerOptions,
    smartFilteredCustomers,
    projectOptions,
    smartFilteredProjects,
  } = useFormDropdownOptions({
    divisions,
    projectId,
    selectedBuckets,
    customerId,
    shouldComputeCustomers: hasCustomerDropdowns,
    shouldComputeProjects: hasProjectDropdowns,
  });

  return (
    <>
      <div className="form-response-container" style={style}>
        { header ?? null }
        {sections?.map(({
          name,
          id: sectionId,
          fields = [],
          hidden,
          settings: {
            defaultCollapsed,
            isDuplicationEnabled = false,
            isCheckAllYesNoEnabled = false,
            hasConditionalRendering: sectionHasConditionalRendering,
            conditionalRenderingFormula: sectionConditionalRenderingFormula,
            isRenamingEnabled = false,
          } = {},
          duplicatedParentId,
        }, sectionIndex) => {
          const timeCardHasCanView = timecardPermissionMap?.[sectionId]?.canView;
          const timeCardHasCanEdit = timecardPermissionMap?.[sectionId]?.canEdit;

          const isDisplayOrUnpermitted = isDisplay
          || (
            sectionPermissionMap
              && !sectionPermissionMap[duplicatedParentId || sectionId]?.canEdit
          )
          || (timecardPermissionMap && !timeCardHasCanEdit);

          if (
            hidden
            || (sectionPermissionMap && !sectionPermissionMap[duplicatedParentId ?? sectionId]?.canView)
            || (timecardPermissionMap && !timeCardHasCanView)
            || (
              sectionHasConditionalRendering
              && FormHelpers.isConditionalRenderingFormulaComplete(sectionConditionalRenderingFormula)
              && !FormHelpers.executeConditionalRenderingFormula({
                formula: sectionConditionalRenderingFormula,
                responses,
              })
            )
          ) {
            return null;
          }
          return (
            <div key={sectionId}>
              <SectionDisplayHeader
                key={`${sectionId}-${name}`}
                name={name}
                sectionIndex={sectionIndex}
                sectionId={sectionId}
                defaultCollapsed={defaultCollapsed}
                isDuplicationEnabled={isDuplicationEnabled}
                isCheckAllYesNoEnabled={isCheckAllYesNoEnabled && !isDisplayOrUnpermitted}
                isRenamingEnabled={isRenamingEnabled}
                duplicatedParentId={duplicatedParentId}
                collapsed={collapsed}
                onCollapsedChanged={onCollapsedChanged}
                copySection={copySection}
                onDeleteClicked={onDeleteClicked}
                checkAllYesNo={checkAllYesNo}
                onSectionRenamed={!isDisplayOrUnpermitted ? onSectionRenamed : null}
              />
              {hasInitialLoaded
                && !collapsed[sectionIndex]
                && fields.map(({ id: fieldId, configProps, selectedType }, idx) => {
                  const { preview } = fieldTypes[selectedType];
                  const {
                    hasConditionalRendering,
                    conditionalRenderingFormula,
                    width: fieldWidth = '100',
                    isHyperlink,
                    type: attributeType,
                  } = configProps;

                  const widthStyle = fieldWidth === '50' ? '50%' : '100%';

                  // If a field has conditional rendering, and the formula is complete,
                  // and the formula returns false, render empty placeholder to preserve layout
                  if (
                    hasConditionalRendering
                    && FormHelpers.isConditionalRenderingFormulaComplete(
                      conditionalRenderingFormula,
                    ) && !FormHelpers.executeConditionalRenderingFormula({
                      formula: conditionalRenderingFormula,
                      responses,
                    })
                  ) {
                    // For full-width hidden fields or half-width fields on their own,
                    // return null to collapse completely
                    const previousField = fields[idx - 1] ?? {};
                    const nextField = fields[idx + 1] ?? {};
                    const previousWidth = previousField.configProps?.width ?? '100';
                    const nextWidth = nextField.configProps?.width ?? '100';
                    const isHalfWidthOnOwn = fieldWidth === '50' && (previousWidth === '100' && nextWidth === '100');
                    if (fieldWidth === '100' || isHalfWidthOnOwn) {
                      return null;
                    }

                    return (
                      <div
                        key={fieldId}
                        style={{
                          paddingBottom: 20,
                          paddingLeft: 5,
                          paddingRight: 5,
                          width: widthStyle,
                          display: 'inline-block',
                          verticalAlign: 'top',
                          minHeight: 60,
                          visibility: 'hidden',
                        }}
                      />
                    );
                  }

                  return (
                    <div
                      key={fieldId}
                      style={{
                        paddingBottom: 20,
                        paddingLeft: 5,
                        paddingRight: 5,
                        height: collapsed[sectionIndex] ? 0 : 'auto',
                        pointerEvents: dynamicLoading || (
                          isDisplayOrUnpermitted && !interactiveFields.has(selectedType)
                          && !(selectedType === 'text' && isHyperlink)
                          && !(selectedType === 'attribute' && isAddressAttribute(attributeType))
                        ) ? 'none' : 'auto',
                        width: widthStyle,
                        display: 'inline-block',
                        verticalAlign: 'top',
                      }}
                    >
                      {preview({
                        key,
                        id: fieldId,
                        configProps,
                        setResponses: onResponsesChanged,
                        responses,
                        initialResponses,
                        customerOptions,
                        smartFilteredCustomers,
                        projectOptions,
                        smartFilteredProjects,
                        users: divUsers,
                        projects: divisionProjects,
                        equipment: divisionEquipment,
                        customers: customerList,
                        costcodes: divisionCostcodes,
                        costcodesLoading,
                        fetchProjectCostcodes,
                        formTemplates: divisionFormTemplates,
                        phases,
                        scheduleOfValues: scheduleOfValuesSummary,
                        vendors: vendorList,
                        contactAddressBooks,
                        globalAddressBooks,
                        materials: divisionMaterials,
                        projectMaterials,
                        labels,
                        projectEquipment,
                        scheduleCategories,
                        responding: true,
                        onOpenFileModal: setFileAddId,
                        onOpenFile: setSelectedFile,
                        setCurrFileFieldId,
                        setCurrAttachmentRowId,
                        setOpenFileIndex: setCurrIndex,
                        setPreviewProps: onPreviewPropsChanged(fieldId),
                        previewProps: previewProps[fieldId],
                        formRef: { current: form },
                        selectedBucket,
                        setBucketId: bucketIdChanged,
                        customerIds,
                        customerId,
                        setCustomerIds,
                        projectIds,
                        projectId: configProps.dataType === 'Costcodes' ? costcodeProjectId : projectId,
                        setProjectIds: projectIdChanged,
                        vendorIds,
                        vendorId,
                        setVendorIds,
                        contactIds,
                        setContactIds,
                        costcodeIds,
                        setCostcodeIds,
                        userIds,
                        setUserIds,
                        subContractIds,
                        setSubContractIds,
                        payableSubContractIds,
                        setPayableSubContractIds,
                        equipmentId,
                        setEquipmentId,
                        phaseId,
                        setPhaseId,
                        customTables,
                        userToLabel,
                        customerToLabel,
                        vendorToLabel,
                        locked: templateProjectId,
                        changeOrderMap,
                        subContractMap,
                        subContractCOMap,
                        customerIdMap: customers,
                        projectIdMap,
                        costcodeIdMap,
                        phaseIdMap,
                        userIdMap,
                        equipmentIdMap,
                        vendorIdMap: vendors,
                        divisions,
                        divisionMap,
                        templateId,
                        t,
                        projectTypes,
                        equipmentTypes,
                        equipmentTypeMap,
                        isDisplay: isDisplayOrUnpermitted,
                        fileMap,
                        sections,
                        forms,
                        subContractForms,
                        parentForm,
                        editing: true,
                        setCurrFieldId,
                        boardCardsForDropdown,
                        bucketTemplateMap,
                        createTimeTableIds,
                        selectedBucketTypeToIdMap,
                        selectedBucketTypes,
                        settings,
                        globalFileMap,
                        hasBucketsLoaded,
                        selectedProject,
                        initialProjectIds,
                        timecardProjectId,
                        dropdownFilterProjectIds,
                        onFieldTrigger,
                        relevantGPSBucketFieldMap,
                        isExternalForm,
                        sectionId,
                        positionNames,
                        buckets,
                        bucketTemplates,
                        statuses,
                        assignedProjectIds,
                        boardsForDropdown,
                      })}
                      {errors[fieldId] && (
                        <Text
                          style={{
                            color: Colors.ONTRACCR_RED,
                          }}
                        >
                          {errors[fieldId]}
                        </Text>
                      )}
                    </div>
                  );
                })}
            </div>
          );
        })}
        {
          dynamicLoading && (
            <Row
              style={{ position: 'absolute', inset: 0 }}
              justify="center"
              align="middle"
            >
              <Col>
                <Spin />
              </Col>
            </Row>
          )
        }
      </div>
      <FullPhoto
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...selectedFile}
        rotation={selectedFile?.rotation !== undefined
          ? selectedFile.rotation
          : (fileMap?.[selectedFile?.id]?.rotation ?? 0)}
        file={globalFileMap[selectedFile?.id || selectedFile?.autoSaveId]}
        type={selectedFileType}
        onClose={onCloseFile}
        onLeft={onNextFileClick(false)}
        onRight={onNextFileClick(true)}
        showLeft={selectedFile && currIndex > 0}
        showRight={selectedFile
          && currentFiles.length > 0
          && currIndex < currentFiles.length - 1}
        onDownload={onDownloadFile}
        useApryse
      />
      <LiveFeedFileUpload
        visible={!!fileAddId}
        onUploadEnd={onUploadEnd}
        addFile={onAddFile}
        customProps={{
          width: 600,
          zIndex: 1001,
          style: {},
        }}
        placement="right"
        isExternalForm={isExternalForm}
      />
      <CheckAllYesNoModal
        visible={!!checkAllYesNoSectionId}
        onClose={() => {
          setCheckAllYesNoSectionId(null);
        }}
        onConfirm={(isYes) => handleCheckAllYesNoConfirm(isYes, checkAllYesNoSectionId)}
      />
    </>
  );
}

FormResponseFields.propTypes = {
  mode: PropTypes.string.isRequired,
  errors: PropTypes.objectOf(PropTypes.string),
  sections: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      configProps: PropTypes.shape({}),
      selectedType: PropTypes.string,
    })),
    duplicatedParentId: PropTypes.string,
  })),
  fileMap: PropTypes.objectOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
  })),
  initialData: PropTypes.shape({}),
  initialCustomerIds: PropTypes.arrayOf(PropTypes.string),
  initialProjectIds: PropTypes.arrayOf(PropTypes.string),
  initialCostcodeIds: PropTypes.arrayOf(PropTypes.string),
  initialVendorIds: PropTypes.arrayOf(PropTypes.string),
  initialBucketId: PropTypes.string,
  initialCardId: PropTypes.string,
  initialCardTitle: PropTypes.string,
  initialCardBoardId: PropTypes.string,
  initialEquipmentId: PropTypes.string,
  initialEquipmentTitle: PropTypes.string,
  templateProjectId: PropTypes.string,
  templateCustomerId: PropTypes.string,
  visible: PropTypes.bool,
  selectedFormId: PropTypes.string,
  responses: PropTypes.shape({}),
  onResponsesChanged: PropTypes.func,
  onCustomerIdChanged: PropTypes.func,
  onProjectIdChanged: PropTypes.func,
  onVendorIdChanged: PropTypes.func,
  onBucketIdChanged: PropTypes.func,
  style: PropTypes.shape({}),
  header: PropTypes.node,
  divisions: PropTypes.arrayOf(PropTypes.string),
  isExternalForm: PropTypes.bool.isRequired,
  templateId: PropTypes.string,
  onResponseSectionsChanged: PropTypes.func.isRequired,
  isDisplay: PropTypes.bool,
  sectionPermissionMap: PropTypes.shape({}),
  parentForm: PropTypes.shape({}),
  createTimeTableIds: PropTypes.arrayOf(PropTypes.string),
  selectedProject: PropTypes.shape({}),
  timecardProjectId: PropTypes.string,
  dropdownFilterProjectIds: PropTypes.arrayOf(PropTypes.string),
  onFieldTrigger: PropTypes.func,
  timecardPermissionMap: PropTypes.shape({}),
  formUserIds: PropTypes.arrayOf(PropTypes.string),
  onFieldsInitialLoadedChange: PropTypes.func,
};

FormResponseFields.defaultProps = {
  errors: {},
  sections: undefined,
  fileMap: null,
  initialData: null,
  initialCustomerIds: [],
  initialProjectIds: [],
  initialCostcodeIds: [],
  initialVendorIds: [],
  initialBucketId: null,
  initialCardId: null,
  initialCardTitle: null,
  initialCardBoardId: null,
  initialEquipmentId: null,
  initialEquipmentTitle: null,
  templateProjectId: null,
  templateCustomerId: null,
  visible: false,
  selectedFormId: null,
  responses: undefined,
  onResponsesChanged: null,
  onCustomerIdChanged: null,
  onProjectIdChanged: null,
  onVendorIdChanged: null,
  onBucketIdChanged: null,
  style: {},
  header: null,
  divisions: [],
  templateId: null,
  isDisplay: false,
  sectionPermissionMap: null,
  parentForm: undefined,
  createTimeTableIds: [],
  selectedProject: null,
  timecardProjectId: null,
  dropdownFilterProjectIds: null,
  onFieldTrigger: null,
  timecardPermissionMap: null,
  formUserIds: [],
  onFieldsInitialLoadedChange: null,
};
